import * as React from "react";
import { Box, Grid, Paper, Popover, Typography } from "@mui/material";

type Props = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onSelect: (rows: number, cols: number) => void;
  onHover: (rows: number, cols: number) => void;
};

const MAX_GRID_SIZE = 10;

export const SpotTilePicker: React.FC<Props> = ({
  anchorEl,
  open,
  onClose,
  onSelect,
  onHover,
}) => {
  const [hoveredCell, setHoveredCell] = React.useState<{
    row: number;
    col: number;
  } | null>(null);

  const handleCellHover = (row: number, col: number) => {
    setHoveredCell({ row, col });
    onHover(row + 1, col + 1); // +1 because we want 1-indexed for display
  };

  const handleCellClick = (row: number, col: number) => {
    onSelect(row + 1, col + 1); // +1 because we want 1-indexed
    onClose();
  };

  const handleClose = () => {
    setHoveredCell(null);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <Box sx={{ p: 2 }}>
        <Grid container spacing={0.5} sx={{ width: 220 }}>
          {Array.from({ length: MAX_GRID_SIZE }).map((_, row) => (
            <React.Fragment key={row}>
              {Array.from({ length: MAX_GRID_SIZE }).map((_, col) => {
                const isHighlighted =
                  hoveredCell &&
                  row <= hoveredCell.row &&
                  col <= hoveredCell.col;

                return (
                  <Grid item xs={1.2} key={`${row}-${col}`}>
                    <Paper
                      elevation={isHighlighted ? 2 : 0}
                      sx={{
                        width: 18,
                        height: 18,
                        backgroundColor: isHighlighted
                          ? "primary.main"
                          : "action.hover",
                        cursor: "pointer",
                        transition: "all 0.1s",
                        "&:hover": {
                          backgroundColor: "primary.light",
                        },
                      }}
                      onMouseEnter={() => handleCellHover(row, col)}
                      onClick={() => handleCellClick(row, col)}
                    />
                  </Grid>
                );
              })}
            </React.Fragment>
          ))}
        </Grid>
        <Typography
          variant="body2"
          align="center"
          sx={{ mt: 1, fontWeight: "bold" }}
        >
          {hoveredCell
            ? `${hoveredCell.row + 1} × ${hoveredCell.col + 1}`
            : "Hover to select"}
        </Typography>
      </Box>
    </Popover>
  );
};
